import * as React from "react";
import { SkuClient } from "../utils/scanner-backend";
import { ApiConfig } from "../utils/AuthorizedApiBase";

// LocalStorage key for character map persistence
const CHARACTER_MAP_LOCALSTORAGE_KEY = "scanner-character-map-source";
const CHARACTER_MAP_TARGET_KEY = "scanner-character-map-target";

// Prefix characters for target map (special markers)
const TARGET_PREFIX = "@#";

// Initialize API client
const skuClient = new SkuClient(new ApiConfig());

export interface CharacterMapState {
  /** The source character string (scanned from QR code) */
  source: string;
  /** The target character string (all characters used in SKU codes) */
  target: string;
  /** Whether the character map is configured and ready to use */
  isConfigured: boolean;
  /** Whether we're currently loading the target characters from API */
  isLoadingTarget: boolean;
  /** Any error that occurred during initialization */
  error: string | null;
}

export interface CharacterMapActions {
  /** Translate a scanned string using the character map */
  translate: (input: string) => string;
  /** Set the source character mapping (usually from QR code scan) */
  setSource: (source: string) => Promise<void>;
  /** Reload target characters from the backend */
  refreshTarget: () => Promise<void>;
  /** Clear the character map configuration */
  clearConfiguration: () => void;
}

/**
 * Hook for managing character map configuration and translation.
 *
 * The character map allows translation of scanned barcodes from non-standard
 * keyboard layouts (e.g., barcode scanners configured for different languages)
 * to the correct character representation.
 *
 * Workflow:
 * 1. Target characters are loaded from backend (all chars used in SKU codes)
 * 2. User scans a QR code containing the target characters to establish mapping
 * 3. All subsequent scans are automatically translated using the character map
 *
 * @returns Character map state and actions
 */
export function useCharacterMap(): CharacterMapState & CharacterMapActions {
  const [source, setSourceState] = React.useState<string>("");
  const [target, setTargetState] = React.useState<string>("");
  const [isLoadingTarget, setIsLoadingTarget] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);

  // Load stored configuration on mount
  React.useEffect(() => {
    const storedSource = localStorage.getItem(CHARACTER_MAP_LOCALSTORAGE_KEY) || "";
    const storedTarget = localStorage.getItem(CHARACTER_MAP_TARGET_KEY) || "";

    setSourceState(storedSource);
    setTargetState(storedTarget);

    // If we don't have a target yet, fetch it
    if (!storedTarget) {
      loadTargetCharacters();
    }
  }, []);

  // Load target characters from backend
  const loadTargetCharacters = React.useCallback(async () => {
    setIsLoadingTarget(true);
    setError(null);

    try {
      const chars = await skuClient.getUsedCharacters();
      const targetWithPrefix = TARGET_PREFIX + chars;

      setTargetState(targetWithPrefix);
      localStorage.setItem(CHARACTER_MAP_TARGET_KEY, targetWithPrefix);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Nepodarilo sa načítať znaky";
      setError(errorMessage);
      console.error("Error loading target characters:", err);
    } finally {
      setIsLoadingTarget(false);
    }
  }, []);

  // Set source mapping and persist to localStorage
  const setSource = React.useCallback(async (newSource: string) => {
    setSourceState(newSource);
    localStorage.setItem(CHARACTER_MAP_LOCALSTORAGE_KEY, newSource);
  }, []);

  // Translate input string using character map
  const translate = React.useCallback((input: string): string => {
    // If not configured, return input unchanged
    if (!source || !target || source.length !== target.length) {
      // DEBUG: Log why translation is skipped
      console.log('[CharacterMap] Translation skipped - source:', source.length, 'target:', target.length, 'configured:', source.length > 0 && source.length === target.length);
      return input;
    }

    let result = "";
    for (const char of input) {
      const index = source.indexOf(char);
      if (index >= 0 && index < target.length) {
        result += target[index];
      } else {
        result += char;
      }
    }

    // DEBUG: Log translation result
    if (result !== input) {
      console.log('[CharacterMap] Translated:', JSON.stringify(input), '->', JSON.stringify(result));
    }

    return result;
  }, [source, target]);

  // Clear configuration
  const clearConfiguration = React.useCallback(() => {
    setSourceState("");
    setTargetState("");
    localStorage.removeItem(CHARACTER_MAP_LOCALSTORAGE_KEY);
    localStorage.removeItem(CHARACTER_MAP_TARGET_KEY);
  }, []);

  // Character map is configured when source and target have same length
  const isConfigured = source.length > 0 && source.length === target.length;

  return {
    source,
    target,
    isConfigured,
    isLoadingTarget,
    error,
    translate,
    setSource,
    refreshTarget: loadTargetCharacters,
    clearConfiguration,
  };
}
